import { useEffect, useState } from "react"
import {
  MacrosCaloriesPercentWheelPickerPopup,
  PickerHeader,
  PickerHeaderTop,
  PickerHeaderBottom,
  DoneButton,
  PickerViewContainer,
  StyledPickerView,
  PickerSelectedMask,
  MarcoColumn,
  MarcroInfoBox,
  MacroName,
  MacroGram,
  Footer,
  TotalPercent,
  TotalPercentHint,
  PercentValue,
  PercentMaskColumn,
  PercentMaskText,
} from "./styled"
import { FlexBox } from "components/Common/FlexBox"
import {
  CALORIES_PER_CARBS,
  CALORIES_PER_FAT,
  CALORIES_PER_PROTEIN,
  getMarcoGramFromCaloriesPercent,
  sum,
} from "features/profile/hooks/manualEditGoal/helper"
import { IMarcroPercentAndGram } from "features/profile/hooks/manualEditGoal/useManualEditGoalHandlers"
import { formatDisplayNumber, getDisplayCalories } from "utils"

const percentOptions = Array.from({ length: 101 }, (_, index) => ({
  label: `${index}`,
  value: index,
}))

const PopupIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="37"
      height="4"
      viewBox="0 0 37 4"
      fill="rgba(255, 255, 255, 0.20)"
    >
      <rect
        x="0.5"
        width="36"
        height="4"
        rx="2"
        fill="rgba(255, 255, 255, 0.20)"
      />
    </svg>
  )
}

const Header = ({
  onSubmit,
  isDisabled,
}: {
  onSubmit: () => void
  isDisabled: boolean
}) => {
  return (
    <PickerHeader>
      <PickerHeaderTop>
        <PopupIcon />
      </PickerHeaderTop>
      <PickerHeaderBottom>
        <DoneButton
          $isDisabled={isDisabled}
          onClick={() => {
            if (!isDisabled) {
              onSubmit()
            }
          }}
        >
          Done
        </DoneButton>
      </PickerHeaderBottom>
    </PickerHeader>
  )
}

interface IProps {
  onClose: () => void
  visible: boolean
  onSubmit: (value: IMarcroPercentAndGram) => void
  proteinGram: null | number
  fatGram: null | number
  carbsGram: null | number
  proteinCaloriesPercent: null | number
  fatCaloriesPercent: null | number
  carbsCaloriesPercent: null | number
  calories: null | number
}

const MacrosCaloriesPercentWheelPicker = (props: IProps) => {
  const {
    onClose,
    visible,
    onSubmit,
    calories,
    proteinGram,
    fatGram,
    carbsGram,
    proteinCaloriesPercent,
    fatCaloriesPercent,
    carbsCaloriesPercent,
  } = props

  const [internalProteinCaloriesPercent, setInternalProteinCaloriesPercent] =
    useState(proteinCaloriesPercent)
  const [internalProteinGram, setInternalProteinGram] = useState(proteinGram)

  const [internalFatCaloriesPercent, setInternalFatCaloriesPercent] =
    useState(fatCaloriesPercent)
  const [internalFatGram, setInternalFatGram] = useState(fatGram)

  const [internalCarbsCaloriesPercent, setInternalCarbsCaloriesPercent] =
    useState(carbsCaloriesPercent)
  const [internalCarbsGram, setInternalCarbsGram] = useState(carbsGram)

  useEffect(() => {
    setInternalProteinCaloriesPercent(proteinCaloriesPercent)
    setInternalProteinGram(proteinGram)

    setInternalFatCaloriesPercent(fatCaloriesPercent)
    setInternalFatGram(fatGram)

    setInternalCarbsCaloriesPercent(carbsCaloriesPercent)
    setInternalCarbsGram(carbsGram)
  }, [
    proteinCaloriesPercent,
    fatCaloriesPercent,
    carbsCaloriesPercent,
    calories,
    proteinGram,
    fatGram,
    carbsGram,
  ])

  const recaculteInternalProteinGram = () => {
    setInternalProteinGram(
      getMarcoGramFromCaloriesPercent({
        calories: calories,
        percent: internalProteinCaloriesPercent!,
        macroCaloriesPerGram: CALORIES_PER_PROTEIN,
      }),
    )
  }

  const recaculteInternalFatGram = () => {
    setInternalFatGram(
      getMarcoGramFromCaloriesPercent({
        calories: calories,
        percent: internalFatCaloriesPercent!,
        macroCaloriesPerGram: CALORIES_PER_FAT,
      }),
    )
  }

  const recaculteInternalCarbsGram = () => {
    setInternalCarbsGram(
      getMarcoGramFromCaloriesPercent({
        calories: calories,
        percent: internalCarbsCaloriesPercent!,
        macroCaloriesPerGram: CALORIES_PER_CARBS,
      }),
    )
  }

  const onInternalProteinCaloriesPercentChange = (value: number) => {
    setInternalProteinCaloriesPercent(value)
    setInternalProteinGram(
      getMarcoGramFromCaloriesPercent({
        calories: calories,
        percent: value,
        macroCaloriesPerGram: CALORIES_PER_PROTEIN,
      }),
    )
    recaculteInternalFatGram()
    recaculteInternalCarbsGram()
  }

  const onInternalFatCaloriesPercentChange = (value: number) => {
    setInternalFatCaloriesPercent(value)
    setInternalFatGram(
      getMarcoGramFromCaloriesPercent({
        calories: calories,
        percent: value,
        macroCaloriesPerGram: CALORIES_PER_FAT,
      }),
    )
    recaculteInternalCarbsGram()
    recaculteInternalProteinGram()
  }

  const onInternalCarbsCaloriesPercentChange = (value: number) => {
    setInternalCarbsCaloriesPercent(value)
    setInternalCarbsGram(
      getMarcoGramFromCaloriesPercent({
        calories: calories,
        percent: value,
        macroCaloriesPerGram: CALORIES_PER_CARBS,
      }),
    )

    recaculteInternalProteinGram()
    recaculteInternalFatGram()
  }

  const internalTotalPercent = sum(
    internalProteinCaloriesPercent,
    internalFatCaloriesPercent,
    internalCarbsCaloriesPercent,
  )

  const isTotalPercentValid = internalTotalPercent === 100

  return (
    <MacrosCaloriesPercentWheelPickerPopup
      onClose={onClose}
      closeOnMaskClick={true}
      visible={visible}
      bodyStyle={{
        borderRadius: "12px 12px 0 0",
      }}
    >
      <Header
        onSubmit={() => {
          onSubmit({
            proteinCaloriesPercent: internalProteinCaloriesPercent ?? 0,
            fatCaloriesPercent: internalFatCaloriesPercent ?? 0,
            carbsCaloriesPercent: internalCarbsCaloriesPercent ?? 0,
            proteinGram: internalProteinGram,
            fatGram: internalFatGram,
            carbsGram: internalCarbsGram,
          })
        }}
        isDisabled={!isTotalPercentValid}
      />

      <FlexBox style={{ marginTop: 8 }}>
        <MarcoColumn>
          <MarcroInfoBox>
            <MacroName>Protein</MacroName>
            <MacroGram>{formatDisplayNumber(internalProteinGram)} g</MacroGram>
          </MarcroInfoBox>
        </MarcoColumn>
        <MarcoColumn>
          <MarcroInfoBox>
            <MacroName>Fat</MacroName>
            <MacroGram>{formatDisplayNumber(internalProteinGram)} g</MacroGram>
          </MarcroInfoBox>
        </MarcoColumn>
        <MarcoColumn>
          <MarcroInfoBox>
            <MacroName>Carb</MacroName>
            <MacroGram>{formatDisplayNumber(internalCarbsGram)} g</MacroGram>
          </MarcroInfoBox>
        </MarcoColumn>
      </FlexBox>

      <PickerViewContainer>
        <MarcoColumn>
          <StyledPickerView
            columns={[percentOptions]}
            value={[internalProteinCaloriesPercent]}
            onChange={(value: any) => {
              onInternalProteinCaloriesPercentChange(value[0] as number)
            }}
          />
        </MarcoColumn>

        <MarcoColumn>
          <StyledPickerView
            columns={[percentOptions]}
            value={[internalFatCaloriesPercent]}
            onChange={(value: any) => {
              onInternalFatCaloriesPercentChange(value[0] as number)
            }}
          />
        </MarcoColumn>

        <MarcoColumn>
          <StyledPickerView
            columns={[percentOptions]}
            value={[internalCarbsCaloriesPercent]}
            onChange={(value: any) => {
              onInternalCarbsCaloriesPercentChange(value[0] as number)
            }}
          />
        </MarcoColumn>

        <PickerSelectedMask>
          <FlexBox style={{ height: 39 }}>
            <PercentMaskColumn>
              <PercentMaskText $leftOffset={12}>%</PercentMaskText>
            </PercentMaskColumn>

            <PercentMaskColumn>
              <PercentMaskText $leftOffset={14.5}>%</PercentMaskText>
            </PercentMaskColumn>

            <PercentMaskColumn>
              <PercentMaskText $leftOffset={17}>%</PercentMaskText>
            </PercentMaskColumn>
          </FlexBox>
        </PickerSelectedMask>
      </PickerViewContainer>

      <Footer>
        <div>
          <TotalPercent>% Total</TotalPercent>
          <TotalPercentHint $isValid={isTotalPercentValid}>
            Macronutrients must equal 100%
          </TotalPercentHint>
        </div>

        <PercentValue $isValid={isTotalPercentValid}>
          {internalTotalPercent}%
        </PercentValue>
      </Footer>
    </MacrosCaloriesPercentWheelPickerPopup>
  )
}

export default MacrosCaloriesPercentWheelPicker
